import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../Config/firebase";
import { signInWithEmailAndPassword } from "../../Config/matuAuth";
import FootballLoading from "../../Utils/FootballLoading";

// Componente de icono reutilizable
const Icon = ({ children, className = "" }) => (
  <div className={`flex items-center justify-center ${className}`}>
    {children}
  </div>
);

// Componente de input con icono
const InputField = ({ 
  type, 
  id, 
  label, 
  placeholder, 
  value, 
  onChange, 
  icon, 
  required = false 
}) => (
  <div className="space-y-2 group">
    <label
      htmlFor={id}
      className="block text-gray-300 text-sm font-medium pl-1 transition-colors duration-200 group-focus-within:text-red-400"
    >
      {label}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <div className="h-5 w-5 text-gray-500 transition-colors duration-200 group-focus-within:text-red-400">
          {icon}
        </div>
      </div>
      <input
        type={type}
        id={id}
        className="w-full pl-10 pr-4 py-3 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 hover:bg-gray-800/90 backdrop-blur-sm"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
      />
    </div>
  </div>
);

// Componente de error
const ErrorMessage = ({ message }) => (
  <div className="mb-6 p-3 bg-red-900/30 border border-red-800 rounded-lg animate-in slide-in-from-top-2 duration-300">
    <p className="text-red-400 text-sm flex items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 mr-2 animate-pulse"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      {message}
    </p>
  </div>
);

// Componente de loading: usa el splash de marca único (corto y consistente)
const LoadingScreen = () => (
  <FootballLoading message="Iniciando sesión…" />
);

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const navigate = useNavigate();

  // Configuración de mensajes de error
  const errorMessages = {
    'auth/user-not-found': 'No existe una cuenta con este correo electrónico.',
    'auth/wrong-password': 'Contraseña incorrecta. Inténtalo de nuevo.',
    'auth/too-many-requests': 'Demasiados intentos fallidos. Intenta más tarde o restablece tu contraseña.',
    'default': 'No se pudo iniciar sesión. Verifica tus credenciales e intenta nuevamente.'
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    
    // Animación de entrada del formulario
    const timer = setTimeout(() => setIsFormVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Manejar recordar email
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      localStorage.setItem("userUUID", user.uid);
      navigate("/prediccion-zone/mi-zona");
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      setError(errorMessages[error.code] || errorMessages.default);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800 relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradientes flotantes */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Partículas animadas */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/10 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Tarjeta principal */}
      <div className={`bg-gray-900/80 backdrop-blur-xl p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-800/50 relative overflow-hidden transform transition-all duration-700 ${
        isFormVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}>
        
        {/* Elementos decorativos mejorados */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>

        {/* Contenido */}
        <div className="relative z-10">
          {/* Logo y encabezado */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative group">
              <img
                src="/Dprologo.png"
                alt="Logo"
                className="w-24 h-24 rounded-full border-4 border-red-500 shadow-2xl mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-red-500/50"
              />
              <div className="absolute inset-0 rounded-full border-4 border-red-500/50 animate-ping"></div>
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              DeportivosPro
            </h1>
            <div className="w-16 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mb-4 animate-pulse"></div>
            <h2 className="text-2xl font-medium text-gray-200 animate-in slide-in-from-bottom-2 duration-500 delay-300">
              Iniciar Sesión
            </h2>
          </div>

          {/* Mensaje de error */}
          {error && <ErrorMessage message={error} />}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputField
              type="email"
              id="email"
              label="Correo electrónico"
              placeholder="nombre@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              }
            />

            <InputField
              type="password"
              id="password"
              label="Contraseña"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
            />

            {/* Opciones adicionales */}
            <div className="flex items-center justify-between">
              <div className="flex items-center group">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-700 rounded bg-gray-800 transition-all duration-200 hover:scale-110"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-400 cursor-pointer group-hover:text-gray-300 transition-colors duration-200"
                >
                  Recordarme
                </label>
              </div>
              <a
                href="/prediccion-zone/reinicio"
                className="text-sm font-medium text-red-500 hover:text-red-400 transition-all duration-200 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            {/* Botón de envío */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-4 rounded-lg font-medium shadow-lg hover:from-red-500 hover:to-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
            >
              <span className="relative z-10">Iniciar Sesión</span>
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>
          </form>

          {/* Enlaces de ayuda */}
          <div className="mt-6 text-center animate-in slide-in-from-bottom-2 duration-700 delay-700">
            <p className="text-gray-400">
              ¿No tienes una cuenta?{" "}
              <a
                href="/prediccion-zone/join"
                className="font-medium text-red-500 hover:text-red-400 transition-all duration-200 hover:underline"
              >
                Regístrate ahora
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;